/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Chip,
  ChipProps,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Selection,
  SelectionMode,
  SortDescriptor,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
  User,
} from '@heroui/react'
import type { Column } from '@qse/constants'
import type { Key } from '@react-types/shared'
import type { SVGProps } from 'react'
import React from 'react'

import PaginationComponent from '../pagination'

export type IconSvgProps = SVGProps<SVGSVGElement> & {
  size?: number
}

export function capitalize(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''
}

export const PlusIcon = ({ size = 24, width, height, ...props }: IconSvgProps) => {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      height={size || height}
      role="presentation"
      viewBox="0 0 24 24"
      width={size || width}
      {...props}
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
      >
        <path d="M6 12h12" />
        <path d="M12 18V6" />
      </g>
    </svg>
  )
}

export const VerticalDotsIcon = ({ size = 24, width, height, ...props }: IconSvgProps) => {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      height={size || height}
      role="presentation"
      viewBox="0 0 24 24"
      width={size || width}
      {...props}
    >
      <path
        d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
        fill="currentColor"
      />
    </svg>
  )
}

export const SearchIcon = (props: IconSvgProps) => {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      height="1em"
      role="presentation"
      viewBox="0 0 24 24"
      width="1em"
      {...props}
    >
      <path
        d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        d="M22 22L20 20"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  )
}

export const ChevronDownIcon = ({ strokeWidth = 1.5, ...otherProps }: IconSvgProps) => {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      height="1em"
      role="presentation"
      viewBox="0 0 24 24"
      width="1em"
      {...otherProps}
    >
      <path
        d="m19.92 8.95-6.52 6.52c-.77.77-2.03.77-2.8 0L4.08 8.95"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit={10}
        strokeWidth={strokeWidth}
      />
    </svg>
  )
}

export const statusOptions = [
  { name: 'Active', uid: 'active' },
  { name: 'Paused', uid: 'paused' },
  { name: 'Vacation', uid: 'vacation' },
]

const statusColorMap: Record<string, ChipProps['color']> = {
  active: 'success',
  paused: 'danger',
  vacation: 'warning',
}

const INITIAL_VISIBLE_COLUMNS = [
  'companyName',
  'firstName',
  'lastName',
  'email',
  'phone',
  'noOfClinic',
  'totalPayout',
  'airclinicEarn',
  'admins',
  'actions',
]

export default function TableComponent({
  name = 'data',
  showTopContent = true,
  data = [],
  showBottomContent = true,
  selectionMode = 'multiple',
  initialVisibleColumns = INITIAL_VISIBLE_COLUMNS,
  customRenderCell,
  columns,
  total = 0,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onLimitChange,
  isLoading,
  high,
  searchColumn = 'name',
  onRowSelect,
  onRowDoubleClick,
  disableLocalPagination = false,
  rowsPerPage: externalRowsPerPage,
  trClassName,
  isRowLoading,
  sortBy,
  sortDirection,
  onSortChange,
  sortDescriptor: externalSortDescriptor,
}: {
  name?: string
  showTopContent?: boolean
  showBottomContent?: boolean
  selectionMode?: SelectionMode
  initialVisibleColumns?: string[]
  data: any
  customRenderCell?: (data: Column, columnKey: Column['uid']) => React.ReactNode
  columns: any
  total?: number
  currentPage?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  onLimitChange?: (limit: number) => void
  isLoading?: boolean
  searchColumn?: string
  high?: string
  onRowSelect?: any
  onRowDoubleClick?: any
  disableLocalPagination?: boolean
  rowsPerPage?: number
  trClassName?: string | ((item: any) => string)
  isRowLoading?: (item: any) => boolean
  sortBy?: string
  sortDirection?: 'asc' | 'desc' | ''
  onSortChange?:
    | ((sortBy: string, direction: 'asc' | 'desc') => void)
    | ((sortDescriptor: SortDescriptor) => void)
  sortDescriptor?: SortDescriptor
}) {
  const [filterValue, setFilterValue] = React.useState('')
  const [selectedKeys, setSelectedKeys] = React.useState<Selection>(new Set([]))
  const [visibleColumns, setVisibleColumns] = React.useState<Selection>(
    initialVisibleColumns.length > 0 ? new Set(initialVisibleColumns) : 'all',
  )
  const [statusFilter, setStatusFilter] = React.useState<Selection>('all')
  const [rowsPerPage, setRowsPerPage] = React.useState(10)

  // Use rowsPerPage from props when local pagination is disabled
  const effectiveRowsPerPage = disableLocalPagination ? externalRowsPerPage || 10 : rowsPerPage

  // Use external sorting when provided, otherwise use internal state
  const [internalSortDescriptor, setInternalSortDescriptor] = React.useState<SortDescriptor>({
    column: 'age',
    direction: 'ascending',
  })

  const effectiveSortDescriptor = React.useMemo(() => {
    // Priority: externalSortDescriptor > sortBy/sortDirection > internalSortDescriptor
    if (externalSortDescriptor) {
      return externalSortDescriptor
    }
    if (sortBy && sortDirection) {
      return {
        column: sortBy,
        direction: sortDirection === 'asc' ? ('ascending' as const) : ('descending' as const),
      }
    }

    // Return undefined when no sort is active to indicate unsorted state
    return undefined
  }, [externalSortDescriptor, sortBy, sortDirection, internalSortDescriptor])

  const handleSortChange = React.useCallback(
    (descriptor: SortDescriptor) => {
      if (onSortChange) {
        // Determine format based on which props were provided
        // If both sortBy and sortDirection props were provided (even if empty), use OLD format
        // If sortDescriptor prop was provided (even if undefined), use NEW format
        // Check by seeing if sortBy prop exists at all
        const useOldFormat = sortBy !== undefined && sortDirection !== undefined

        if (!useOldFormat) {
          // New format for storage-report.tsx and plan-management.tsx
          ;(onSortChange as (sortDescriptor: SortDescriptor) => void)(descriptor)
        } else {
          // Old format for tenant-management.tsx
          const direction = descriptor.direction === 'ascending' ? 'asc' : 'desc'

          // Extract column key - handle both string and object cases
          let column: string

          if (typeof descriptor.column === 'string') {
            column = descriptor.column
          } else if (
            descriptor.column &&
            typeof descriptor.column === 'object' &&
            'uid' in descriptor.column
          ) {
            // If column is an object, try to extract uid
            column = (descriptor.column as any).uid
          } else {
            console.error('Invalid column format:', descriptor.column)

            return
          }

          // Validate that we have proper values before calling
          if (column && direction) {
            ;(onSortChange as (sortBy: string, direction: 'asc' | 'desc') => void)(
              column,
              direction,
            )
          }
        }
      } else {
        // Internal sorting control
        setInternalSortDescriptor(descriptor)
      }
    },
    [onSortChange, sortBy, sortDirection],
  )

  // Use external currentPage when disableLocalPagination is true, otherwise use internal page state
  const [internalPage, setInternalPage] = React.useState(1)
  const effectivePage = disableLocalPagination ? currentPage : internalPage

  const hasSearchFilter = Boolean(filterValue)

  const headerColumns = React.useMemo(() => {
    if (visibleColumns === 'all') return columns

    return columns.filter((column: { uid: Key }) => Array.from(visibleColumns).includes(column.uid))
  }, [visibleColumns])

  const filteredItems = React.useMemo(() => {
    let filteredUsers = [...data]

    if (hasSearchFilter) {
      filteredUsers = filteredUsers.filter(data =>
        (data[searchColumn] || '').toString().toLowerCase().includes(filterValue.toLowerCase()),
      )
    }
    if (statusFilter !== 'all' && Array.from(statusFilter).length !== statusOptions.length) {
      filteredUsers = filteredUsers.filter(user => Array.from(statusFilter).includes(user.status))
    }

    return filteredUsers
  }, [data, filterValue, statusFilter, searchColumn])

  const items = React.useMemo(() => {
    // If local pagination is disabled, use data as is (backend handles pagination)
    if (disableLocalPagination) {
      return filteredItems
    }

    if (effectiveRowsPerPage === -1 || effectiveRowsPerPage >= filteredItems.length) {
      return filteredItems
    }
    const start = (effectivePage - 1) * effectiveRowsPerPage
    const end = start + effectiveRowsPerPage

    return filteredItems.slice(start, end)
  }, [effectivePage, filteredItems, effectiveRowsPerPage, disableLocalPagination])

  // Use items directly - backend handles all sorting
  const sortedItems = items

  const renderCell = React.useCallback(
    (data: any, columnKey: Column['uid']) => {
      if (customRenderCell) {
        return customRenderCell(data, columnKey)
      }

      const cellValue = data['name']

      switch (columnKey) {
        case 'name':
          return (
            <User
              avatarProps={{ radius: 'lg', src: data.avatar }}
              description={data.email}
              name={cellValue}
            >
              {data.email}
            </User>
          )
        case 'role':
          return (
            <div className="flex flex-col">
              <p className="text-bold text-small capitalize">{cellValue}</p>
              <p className="text-bold text-tiny capitalize text-default-400">{data.team}</p>
            </div>
          )
        case 'status':
          return (
            <Chip
              className="capitalize"
              color={statusColorMap[data.status]}
              size="sm"
              variant="flat"
            >
              {cellValue}
            </Chip>
          )
        case 'actions':
          return (
            <div className="relative flex justify-end items-center gap-2">
              <Dropdown>
                <DropdownTrigger>
                  <Button isIconOnly size="sm" variant="light">
                    <VerticalDotsIcon className="text-default-300" />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu>
                  <DropdownItem key="view">View</DropdownItem>
                  <DropdownItem key="edit">Edit</DropdownItem>
                  <DropdownItem key="delete">Delete</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          )
        default:
          return cellValue
      }
    },
    [customRenderCell],
  )

  // const onNextPage = React.useCallback(() => {
  //   if (page < pages) {
  //     setPage(page + 1)
  //   }
  // }, [page, pages])

  // const onPreviousPage = React.useCallback(() => {
  //   if (page > 1) {
  //     setPage(page - 1)
  //   }
  // }, [page])

  const onRowsPerPageChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = Number(e.target.value)

      onLimitChange?.(value)
      if (!disableLocalPagination) {
        setInternalPage(1)
      }
    },
    [onLimitChange, disableLocalPagination],
  )

  const onSearchChange = React.useCallback(
    (value?: string) => {
      if (value) {
        setFilterValue(value)
        if (!disableLocalPagination) {
          setInternalPage(1)
        }
      } else {
        setFilterValue('')
      }
    },
    [disableLocalPagination],
  )

  // const onClear = React.useCallback(() => {
  //   setFilterValue('')
  //   setPage(1)
  // }, [])

  const topContent = React.useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between gap-3 items-end">
          <Input
            isClearable
            className="w-full sm:max-w-[44%]"
            placeholder="Search by name..."
            startContent={<SearchIcon />}
            value={filterValue}
            // onClear={() => onClear()}
            onValueChange={onSearchChange}
          />
          <div className="flex gap-3">
            <Dropdown>
              <DropdownTrigger className="hidden sm:flex">
                <Button
                  endContent={<ChevronDownIcon className="text-small" />}
                  radius="sm"
                  variant="flat"
                >
                  Status
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                disallowEmptySelection
                aria-label="Table Columns"
                closeOnSelect={false}
                selectedKeys={statusFilter}
                selectionMode="multiple"
                onSelectionChange={setStatusFilter}
              >
                {statusOptions.map(status => (
                  <DropdownItem key={status.uid} className="capitalize">
                    {capitalize(status.name)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            <Dropdown>
              <DropdownTrigger className="hidden sm:flex">
                <Button
                  endContent={<ChevronDownIcon className="text-small" />}
                  radius="sm"
                  variant="flat"
                >
                  Columns
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                disallowEmptySelection
                aria-label="Table Columns"
                closeOnSelect={false}
                selectedKeys={visibleColumns}
                selectionMode="multiple"
                onSelectionChange={setVisibleColumns}
              >
                {columns.map((column: any) => (
                  <DropdownItem key={column.uid} className="capitalize">
                    {capitalize(column.name)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            <Button color="primary" endContent={<PlusIcon />} radius="sm">
              Add New
            </Button>
          </div>
        </div>
      </div>
    )
  }, [
    filterValue,
    statusFilter,
    visibleColumns,
    onRowsPerPageChange,
    data?.length,
    onSearchChange,
    hasSearchFilter,
  ])

  const bottomContent = React.useMemo(() => {
    if (isLoading) return null

    return (
      <div className="py-2 px-2 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-small text-default-400">Rows per page:</span>
          <Dropdown>
            <DropdownTrigger>
              <Button
                endContent={<ChevronDownIcon className="text-small" />}
                size="sm"
                variant="light"
              >
                {effectiveRowsPerPage}
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Rows per page"
              selectedKeys={new Set([effectiveRowsPerPage.toString()])}
              selectionMode="single"
              onAction={key => {
                setRowsPerPage(Number(key))
                onRowsPerPageChange({
                  target: { value: key },
                } as any)
              }}
            >
              <DropdownItem key="10">10</DropdownItem>
              <DropdownItem key="25">25</DropdownItem>
              <DropdownItem key="100">100</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
        <PaginationComponent
          page={effectivePage}
          total={totalPages}
          onPageChange={onPageChange || (() => {})}
        />
        <div className="hidden sm:flex justify-end gap-2">
          <Button
            isDisabled={currentPage <= 1}
            size="sm"
            variant="flat"
            onPress={() => onPageChange?.(currentPage - 1)}
          >
            Previous
          </Button>
          <Button
            isDisabled={currentPage >= totalPages}
            size="sm"
            variant="flat"
            onPress={() => onPageChange?.(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    )
  }, [
    selectedKeys,
    total,
    currentPage,
    totalPages,
    onPageChange,
    onRowsPerPageChange,
    effectiveRowsPerPage,
    effectivePage,
    isLoading,
  ])

  const classNames = React.useMemo(
    () => ({
      wrapper: `${high} scrollbar-hide bg-transparent shadow-none rounded-none p-0`,
      th: [
        'dark:bg-[#000] !text-[16px] bg-[#f8fafd] border-b text-black dark:text-[#AFAFAF] text-[16px] dark:border-[#2A2A2A] border-[#2A2A2A]/10 !rounded-none',
        'transition-colors duration-200',
      ],
      tr: `!rounded-none transition-colors duration-200`,
      td: [
        'dark:text-[#AFAFAF] text-black',
        // 'hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors duration-200',
      ],
      sortIcon: 'hidden', // Hide HeroUI's default sort icon
      // td: [
      //   // changing the rows border radius
      //   // first
      //   'group-data-[first=true]/tr:first:before:rounded-none dark:group-data-[first=true]/tr:before:bg-[#222]',
      //   'group-data-[first=true]/tr:last:before:rounded-none',
      //   // middle
      //   'group-data-[middle=true]/tr:before:rounded-none dark:group-data-[middle=true]/tr:before:bg-[#222]',
      //   // last
      //   'group-data-[last=true]/tr:first:before:rounded-none dark:group-data-[last=true]/tr:before:bg-[#222]',
      //   'group-data-[last=true]/tr:last:before:rounded-none',
      // ],
    }),
    [],
  )

  const Chevron = ({ isUp }: { isUp: boolean }) => (
    <svg
      aria-hidden="true"
      className={`w-4 h-4 ms-2 mb-px opacity-0 text-inherit inline-block transition-transform-opacity data-[visible=true]:opacity-100 group-data-[hover=true]/th:opacity-100  ${isUp ? 'rotate-180' : ''}`}
      data-direction="ascending"
      data-visible="true"
      fill="none"
      height="1em"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={3}
      viewBox="0 0 24 24"
      width="1em"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )

  return (
    <div>
      {isLoading && (
        <div className="absolute inset-0 bg-transparent flex items-center justify-center z-10">
          <Spinner size="lg" />
        </div>
      )}
      <Table
        isHeaderSticky
        aria-label="Example table with custom cells, pagination and sorting"
        bottomContent={showBottomContent ? bottomContent : undefined}
        bottomContentPlacement="outside"
        classNames={classNames}
        selectedKeys={selectedKeys}
        selectionMode={selectionMode}
        {...(effectiveSortDescriptor && { sortDescriptor: effectiveSortDescriptor })}
        topContent={showTopContent ? topContent : undefined}
        topContentPlacement="outside"
        onSelectionChange={setSelectedKeys}
        onSortChange={handleSortChange}
      >
        <TableHeader columns={headerColumns}>
          {(column: any) => {
            const isSorted = effectiveSortDescriptor?.column === column.uid
            const currentDirection = effectiveSortDescriptor?.direction

            // Show current state and next action on hover
            // Unsorted: nothing -> hover shows up (next: ASC)
            // ASC: up -> hover shows down (next: DESC)
            // DESC: down -> hover shows nothing (next: clear)
            let currentIcon = null
            let hoverIcon = null
            let tooltipContent = ''

            if (column.sortable) {
              if (!isSorted) {
                // Currently unsorted, show nothing, on hover show up chevron
                hoverIcon = (
                  <div className="hidden group-hover:block">
                    <Chevron isUp={true} />
                  </div>
                )
                tooltipContent = 'Sort ascending'
              } else if (currentDirection === 'ascending') {
                // Currently ascending, show up chevron, on hover show down chevron
                currentIcon = (
                  <div className="group-hover:hidden">
                    <Chevron isUp={true} />
                  </div>
                )
                hoverIcon = (
                  <div className="hidden group-hover:block absolute">
                    <Chevron isUp={false} />
                  </div>
                )
                tooltipContent = 'Sort descending'
              } else {
                // Currently descending, show down chevron, on hover show nothing
                currentIcon = (
                  <div className="group-hover:hidden">
                    <Chevron isUp={false} />
                  </div>
                )
                tooltipContent = 'Clear sort'
              }
            }

            return (
              <TableColumn
                key={column.uid}
                align={column.uid === 'actions' ? 'center' : 'start'}
                allowsSorting={column.sortable}
              >
                <div className="flex items-center group">
                  {column.name}
                  {column.sortable &&
                    (tooltipContent ? (
                      <Tooltip closeDelay={0} content={tooltipContent} delay={100} placement="top">
                        <div className="relative ml-1 w-4 h-4 flex justify-center items-center cursor-pointer">
                          {currentIcon}
                          {hoverIcon}
                        </div>
                      </Tooltip>
                    ) : (
                      <div className="relative ml-1 w-4 h-4 flex justify-center items-center">
                        {currentIcon}
                        {hoverIcon}
                      </div>
                    ))}
                </div>
              </TableColumn>
            )
          }}
        </TableHeader>
        <TableBody emptyContent={isLoading ? '' : `No ${name} found`} items={sortedItems}>
          {item => {
            const rowIsLoading = isRowLoading?.(item) || false

            return (
              <TableRow
                key={item.id}
                className={`${typeof trClassName === 'function' ? trClassName(item) : trClassName || ''} ${rowIsLoading ? 'opacity-30 pointer-events-none' : ''}`}
                onClick={() => {
                  if (!rowIsLoading) {
                    onRowSelect?.(item)
                  }
                }}
                onDoubleClick={() => {
                  if (!rowIsLoading) {
                    onRowDoubleClick?.(item)
                  }
                }}
              >
                {columnKey => <TableCell>{renderCell(item, columnKey as Column['uid'])}</TableCell>}
              </TableRow>
            )
          }}
        </TableBody>
      </Table>
    </div>
  )
}
